import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { duesService } from '../services/duesService';
import type {
  CreateDuesSettingRequest,
  UpdateDuesSettingRequest,
  UpdateAssessmentAmountRequest,
  BulkUpdateAssessmentAmountRequest,
  WaiveAssessmentRequest,
  AssessmentFilters,
} from '../types/payments';
import { DUES_KEYS, PAYMENT_KEYS } from './queryKeys';

// Dues settings
export const useDuesSettings = () => {
  return useQuery({
    queryKey: DUES_KEYS.settings,
    queryFn: () => duesService.getDuesSettings(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// User's own dues status
export const useMyDuesStatus = () => {
  return useQuery({
    queryKey: DUES_KEYS.myStatus,
    queryFn: () => duesService.getMyDuesStatus(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Admin dues summary
export const useDuesSummary = (year?: number) => {
  return useQuery({
    queryKey: DUES_KEYS.summary(year),
    queryFn: () => duesService.getAdminDuesSummary(year),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Branch progress (all authenticated users)
export const useBranchProgress = () => {
  return useQuery({
    queryKey: DUES_KEYS.branchProgress,
    queryFn: () => duesService.getBranchProgress(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Assessments list (admin)
export const useAssessmentsList = (duesSettingId: string, filters?: AssessmentFilters) => {
  return useQuery({
    queryKey: DUES_KEYS.assessments(duesSettingId, filters),
    queryFn: () => duesService.getAssessmentsList(duesSettingId, filters),
    enabled: !!duesSettingId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Create dues setting mutation
export const useCreateDuesSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDuesSettingRequest) => duesService.createDuesSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.settings });
    },
  });
};

// Update dues setting mutation
export const useUpdateDuesSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDuesSettingRequest }) =>
      duesService.updateDuesSetting(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.settings });
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
    },
  });
};

// Create bulk assessments mutation
export const useCreateBulkAssessments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      duesSettingId: string;
      userIds?: string[];
      familyMemberIds?: string[];
      sendNotification?: boolean;
    }) => duesService.createBulkAssessments(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
    },
  });
};

// Update assessment amount mutation
export const useUpdateAssessmentAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentId, data }: { assessmentId: string; data: UpdateAssessmentAmountRequest }) =>
      duesService.updateAssessmentAmount(assessmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
    },
  });
};

// Bulk update assessment amount mutation
export const useBulkUpdateAssessmentAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkUpdateAssessmentAmountRequest) =>
      duesService.bulkUpdateAssessmentAmount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
    },
  });
};

// Waive assessment mutation
export const useWaiveAssessment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentId, data }: { assessmentId: string; data: WaiveAssessmentRequest }) =>
      duesService.waiveAssessment(assessmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
    },
  });
};
